import React, { useState } from 'react';
import { HorizontalGridLines, VerticalGridLines, XAxis, VerticalBarSeries, Hint } from 'react-vis';
import 'react-vis/dist/style.css';
import ScrollableXYPlot from './ScrollableXYPlot';
import { collapseDataGaps } from './collapseDataGaps';
import { renderVoidMarkers } from './VoidMarkers';

// Fixed horizontal space per year. Wider than the production chart to accommodate the two clustered
// bars (series + benchmark) shown side by side for each year.
const PX_PER_YEAR = 64;
const BAR_WIDTH = 0.9;
const CHART_MARGIN = { left: 60, right: 20, top: 10, bottom: 55 };

interface DeclineChartProps {
  label: string;
  benchmark: string;
  decline: any[];
  benchmarkDecline?: any[];
  yTitle: string;
  h: number;
  color?: string;
}

const numDigits = (x: number): number => {
  return Math.max(Math.floor(Math.log10(Math.abs(x))), 0) + 1;
};

const formatTick = (x: number): string => {
  const digits = numDigits(x);
  const divisor = digits >= 10 ? 1000000000 : digits >= 7 ? 1000000 : digits >= 4 ? 1000 : 1;
  const suffix = divisor === 1000000000 ? 'B' : divisor === 1000000 ? 'M' : divisor === 1000 ? 'K' : '';
  return (x / divisor).toString().concat(suffix);
};

export const FIRST_DECLINE_COLOR = 'AliceBlue';
export const SECOND_DECLINE_COLOR = 'Cornsilk';

const DeclineChart = (props: DeclineChartProps) => {
  const { label, benchmark, decline, benchmarkDecline, h, yTitle } = props;
  const [mouseValue, setMouseValue] = useState<any>(undefined);

  if (decline?.length < 1) {
    return <h6>No data</h6>;
  }

  const maxYear = new Date().getFullYear() - 1;
  const allXs: number[] = Array.from(new Set([...decline.map((d) => d.x), ...(benchmarkDecline || []).map((d) => d.x)]))
    .filter((year) => year <= maxYear)
    .sort();
  const indexedDecline = decline.reduce((a, c) => ({ ...a, [c.x]: c }), {});
  const patchedDecline = allXs.map((x) => indexedDecline[x] ?? { x, y: 0 });

  const handleOnMouseOver = (value: any, series: string) => {
    setMouseValue({
      x: value.x,
      y: value.y,
      bar: series.startsWith('decline') ? label : benchmark,
      year: value.year ?? value.x,
      decline: value.y.toLocaleString('en-US', { maximumFractionDigits: 1 }) + '%',
    });
  };

  const handleOnMouseOut = () => {
    setMouseValue(null);
  };

  // Collapse runs of missing years into a single labelled column and remap bars onto sequential
  // positions, so data voids don't waste horizontal space.
  const layout = collapseDataGaps(allXs);
  const remapPoint = (d: any) => ({ ...d, x: layout.posByYear.get(d.x), year: d.x });
  const remappedDecline = patchedDecline.map(remapPoint);
  const remappedBenchmark = benchmarkDecline
    ? benchmarkDecline.filter((d) => layout.posByYear.has(d.x)).map(remapPoint)
    : undefined;
  const labelByPos = new Map<number, string>();
  layout.ticks.forEach((t) => {
    labelByPos.set(t.pos, t.label);
  });
  const tickValues = layout.ticks.map((t) => t.pos);

  const plotWidth = layout.columnCount * PX_PER_YEAR;
  const declineYs = [...patchedDecline, ...(benchmarkDecline || [])].map((d) => d.y);
  const rawMin = Math.min(0, ...declineYs);
  const rawMax = Math.max(0, ...declineYs);
  const yPad = Math.max(1, (rawMax - rawMin) * 0.05);
  const yDomain: number[] = [rawMin < 0 ? rawMin - yPad : rawMin, rawMax > 0 ? rawMax + yPad : rawMax];
  // Expand the domain by half a bar on each side so the first and last bars sit flush against the
  // plot edges (no leading/trailing empty space) without being clipped.
  const xDomain = [layout.firstPos - BAR_WIDTH / 2, layout.lastPos + BAR_WIDTH / 2];

  return (
    <ScrollableXYPlot
      height={h}
      plotWidth={plotWidth}
      margin={CHART_MARGIN}
      yDomain={yDomain}
      xDomain={xDomain}
      yTitle={yTitle}
      xTitle="Year"
      yTickFormat={formatTick}
    >
      <VerticalGridLines tickValues={tickValues} />
      <HorizontalGridLines />
      <XAxis tickValues={tickValues} tickFormat={(v: number) => labelByPos.get(v) ?? ''} />
      <VerticalBarSeries
        data={remappedDecline}
        barWidth={BAR_WIDTH}
        yBaseValue={0}
        color={FIRST_DECLINE_COLOR}
        stroke={'#888'}
        onValueMouseOver={(value) => handleOnMouseOver(value, 'decline')}
        onValueMouseOut={handleOnMouseOut}
      />
      {remappedBenchmark ? (
        <VerticalBarSeries
          data={remappedBenchmark}
          barWidth={BAR_WIDTH}
          yBaseValue={0}
          color={SECOND_DECLINE_COLOR}
          stroke={'#888'}
          onValueMouseOver={(value) => handleOnMouseOver(value, 'benchmarkDecline')}
          onValueMouseOut={handleOnMouseOut}
        />
      ) : null}
      {renderVoidMarkers(layout.ticks, yDomain)}
      {mouseValue ? (
        <Hint value={mouseValue}>
          <div className={'rv-hint__content'}>
            {mouseValue.bar}
            <br />
            Year: {mouseValue.year}
            <br />
            Decline: {mouseValue.decline}
          </div>
        </Hint>
      ) : null}
    </ScrollableXYPlot>
  );
};

export default DeclineChart;
